/**
 * Step node styling utilities
 * Shared styling functions for step node component
 */

import { Theme } from '@mui/material';
import {
  getNodeCompletionAnimation,
  nodeCompletionAnimations,
} from '../animations/step-node-animations';
import { STEP_NODE_CONSTANTS } from '../constants/step-node.constants';
import {
  getNodeBackgroundColor,
  getNodeBorder,
  getNodeSvgColor,
  getNodeTextColor,
  NodeState,
} from './node-styling.utils';

/**
 * Base styles for step node
 */
export const getStepNodeStyles = (
  theme: Theme,
  ownerState: NodeState,
  nodeIndex = 0,
  totalNodes = 1,
) => ({
  width: theme.spacing(STEP_NODE_CONSTANTS.SIZE),
  height: theme.spacing(STEP_NODE_CONSTANTS.SIZE),
  borderRadius: STEP_NODE_CONSTANTS.BORDER_RADIUS,
  backgroundColor: getNodeBackgroundColor(theme, ownerState),
  border: getNodeBorder(theme, ownerState),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: getNodeTextColor(theme, ownerState),
  fontSize: theme.typography.pxToRem(STEP_NODE_CONSTANTS.FONT_SIZE),
  fontWeight: theme.typography.fontWeightBold,
  zIndex: `${STEP_NODE_CONSTANTS.Z_INDEX} !important`,
  position: 'relative' as const,
  transition: `${STEP_NODE_CONSTANTS.TRANSITION.PROPERTIES} ${STEP_NODE_CONSTANTS.TRANSITION.DURATION} ${STEP_NODE_CONSTANTS.TRANSITION.EASING}`,
  transformOrigin: STEP_NODE_CONSTANTS.TRANSFORM_ORIGIN,
  // Include completion animation keyframes and apply animation if completed
  ...nodeCompletionAnimations,
  ...getNodeCompletionAnimation(ownerState.completed, nodeIndex, totalNodes),
});

/**
 * Hover effects for step node
 */
export const getStepNodeHoverStyles = () => ({
  '&:hover': {
    transform: `scale(${STEP_NODE_CONSTANTS.HOVER.SCALE})`,
  },
});

/**
 * SVG icon styles with comprehensive overrides
 */
export const getStepNodeSvgStyles = (theme: Theme, ownerState: NodeState) => ({
  '& svg': {
    width: theme.spacing(STEP_NODE_CONSTANTS.ICON_SIZE),
    height: theme.spacing(STEP_NODE_CONSTANTS.ICON_SIZE),
    color: getNodeSvgColor(theme, ownerState),
    transition: `color ${STEP_NODE_CONSTANTS.TRANSITION.DURATION} ${STEP_NODE_CONSTANTS.TRANSITION.EASING}`,
  },
});

/**
 * Comprehensive SVG element overrides
 */
export const getStepNodeSvgOverrideStyles = (
  theme: Theme,
  ownerState: NodeState,
) => {
  const svgColor = getNodeSvgColor(theme, ownerState);
  const important = STEP_NODE_CONSTANTS.SVG_OVERRIDE_PRIORITY;

  return {
    // Use !important and highly specific selectors to override hard-coded SVG fills
    '& path[fill]:not([fill="none"])': {
      fill: `${svgColor} ${important}`,
    },
    '& path[stroke]:not([stroke="none"])': {
      stroke: `${svgColor} ${important}`,
    },
    '& circle[fill]:not([fill="none"])': {
      fill: `${svgColor} ${important}`,
    },
    '& circle[stroke]:not([stroke="none"])': {
      stroke: `${svgColor} ${important}`,
    },
    '& rect[fill]:not([fill="none"])': {
      fill: `${svgColor} ${important}`,
    },
    '& rect[stroke]:not([stroke="none"])': {
      stroke: `${svgColor} ${important}`,
    },
    '& polygon[fill]:not([fill="none"])': {
      fill: `${svgColor} ${important}`,
    },
    '& polygon[stroke]:not([stroke="none"])': {
      stroke: `${svgColor} ${important}`,
    },
    '& line': {
      stroke: `${svgColor} ${important}`,
    },
    '& polyline': {
      stroke: `${svgColor} ${important}`,
    },
  };
};
